import React, { useEffect, useState } from 'react';
import { Upload } from 'antd';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { InboxOutlined } from '@ant-design/icons';
import WarningIcon from '@mui/icons-material/Warning';
import { CircularProgress } from '@mui/material';

import { loadDocumentTypeIdsAndNames } from '../../../store/directusService';
import {
    addParticipantDocuments,
    clearValidationErrors,
    onClearDocumentErrors
} from '../../../store/documentService';
import { clearDarEntry } from '../../../store/participantService';
import Button from '../../formElements/Button';
import ScreenError from '../../ui/messageBanners/ScreenError';

import { CREATE_DAR_SUCCESS_MESSAGE } from './CreateDar';

import '../../../commonStyles/antUploaderStyles.css';
import form from '../../../commonStyles/formStyles.module.css';
import uploader from '../../../commonStyles/uploader.module.css';
import local from '../homeStyles/dar.module.css';

const { Dragger } = Upload;
export const FILE_TYPES = '.doc, .docx, .pdf, .csv, .rtf, .jpg, .png, .odt, .ods';
const _MAX_FILE_SIZE = 25;
const acceptedFileTypes = [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/pdf',
    'text/csv',
    'application/rtf',
    'image/jpeg',
    'image/png',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.oasis.opendocument.spreadsheet'
];

export const FILE_SIZE_ERROR_MESSAGE = `Total file upload size must be smaller than ${_MAX_FILE_SIZE}MB`;

const DarDocumentUploader = ({ onUploadErrors, onPreUploadErrors, isOpenUploader, onExit }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [successMsg, setSuccessMsg] = useState('');
    const [isPending, setIsPending] = useState(false);
    const [darDocumentId, setDarDocumentId] = useState('');
    const [fileList, setFileList] = useState([]);
    const [backUpFileList, setBackUpFileList] = useState([]);
    const [totalQueueSize, setTotalQueueSize] = useState(0);
    const [wrongFileTypes, setWrongFileTypes] = useState([]);
    const [postUploadWarnings, setPostUploadWarnings] = useState([]);
    const [preUploadErrors, setPreUploadErrors] = useState({});
    const [postUploadErrors, setPostUploadErrors] = useState({});
    const [showRetryButton, setShowRetryButton] = useState(false);
    const [isRetryDisabled, setIsRetryDisabled] = useState(false);

    // STORE STATE
    const participantDocumentErrors = useSelector(
        (state) => state.entities.documentService.participantDocumentErrors
    );
    const darEntry = useSelector((state) => state.entities.participantService.darEntry);
    const documentTypeIdsAndNames = useSelector(
        (state) => state.entities.directusService.documentTypeIdsAndNames
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const validationErrors = useSelector(
        (state) => state.entities.documentService.validationErrors
    );

    // VENDOR FN
    const uploadProps = {
        name: 'file',
        onRemove(e) {
            const updatedList = fileList.filter((el) => el.uid !== e.uid);
            setFileList(updatedList);
            if (updatedList.length < 1) {
                setPreUploadErrors({});
                setPostUploadErrors({});
                setWrongFileTypes([]);
            }
            if (isValidSize(totalQueueSize - e.size)) clearError('fileSize');
            setTotalQueueSize((prev) => prev - e.size);
        },
        onDrop(e) {
            isRetryDisabled &&
                Object.keys(postUploadErrors).length < 1 &&
                setIsRetryDisabled(false);
            const allowedFiles = Object.values(e.dataTransfer.files).filter(
                (el) =>
                    acceptedFileTypes.includes(el.type) &&
                    !fileList.find((entry) => entry.name === el.name)
            );
            let wrongTypes = Object.values(e.dataTransfer.files).filter(
                (el) => !acceptedFileTypes.includes(el.type)
            );
            wrongTypes = [...wrongTypes, ...wrongFileTypes];
            const totalSize = allowedFiles.map((el) => el.size).reduce((acc, cur) => acc + cur, 0);
            setTotalQueueSize((prev) => prev + totalSize);

            !isValidSize(totalSize) && configFileError('fileSize', FILE_SIZE_ERROR_MESSAGE);
            wrongTypes.length > 0 && setWrongFileTypes(wrongTypes);
            allowedFiles.length > 0 && setFileList((prev) => [...prev, ...allowedFiles]);
        },
        multiple: true,
        customRequest(info) {
            isRetryDisabled &&
                Object.keys(postUploadErrors).length < 1 &&
                setIsRetryDisabled(false);
            if (!acceptedFileTypes.includes(info.file.type)) {
                setWrongFileTypes([info.file, ...wrongFileTypes]);
            } else if (!fileList.find((entry) => entry.name === info.file.name)) {
                !isValidSize(info.file.size) &&
                    configFileError('fileSize', FILE_SIZE_ERROR_MESSAGE);
                setTotalQueueSize((prev) => prev + info.file.size);
                setFileList((prev) => [...prev, info.file]);
            }
        }
    };

    // USEEFFECTS
    useEffect(() => {
        documentTypeIdsAndNames?.length < 1 && dispatch(loadDocumentTypeIdsAndNames());
    }, []);

    useEffect(() => {
        if (!documentTypeIdsAndNames || documentTypeIdsAndNames?.length < 1) return;
        const darDocument = documentTypeIdsAndNames.find(
            (el) => el.name.toLowerCase() === 'data amendment request'
        );
        darDocument && setDarDocumentId(darDocument.id);
    }, [documentTypeIdsAndNames]);

    useEffect(() => {
        const isErrors = Object.values(postUploadErrors).some((el) => el.error);
        if (isErrors && Object.keys(darEntry)?.length > 1) {
            onUploadErrors();
            setFileList([...backUpFileList]);
            setShowRetryButton(true);
        }
        setIsRetryDisabled(isErrors || fileList.length < 1);
    }, [postUploadErrors]);

    useEffect(() => {
        if (postUploadWarnings?.length > 0) onUploadErrors();
    }, [postUploadWarnings]);

    useEffect(() => {
        onPreUploadErrors(Object.values(preUploadErrors).some((el) => el.error));
    }, [preUploadErrors]);

    useEffect(() => {
        if (!isOpenUploader) clearUploader();
    }, [isOpenUploader]);

    useEffect(() => {
        setIsPending(false);
        if (successMessage === CREATE_DAR_SUCCESS_MESSAGE) {
            if (Object.keys(darEntry)?.length > 1) onUpload();
        } else if (validationErrors && Object.keys(validationErrors).length > 0) {
            const errors = Object.entries(validationErrors).reduce(
                (acc, [key, val]) => ({
                    ...acc,
                    [key]: { ...val, message: `${key}: ${val.message}` }
                }),
                {}
            );
            setPostUploadErrors(errors);
            dispatch(clearValidationErrors());
        } else if (participantDocumentErrors?.length > 0) {
            setPostUploadWarnings(participantDocumentErrors);
            dispatch(onClearDocumentErrors());
        } else if (
            !participantDocumentErrors?.length &&
            !postUploadWarnings?.length &&
            successMessage !== '' &&
            successMessage === successMsg
        ) {
            clearUploader();
        }
    }, [successMessage, participantDocumentErrors, validationErrors, darEntry]);

    // FNs
    const configFileError = (key, message) => {
        setPreUploadErrors((prev) => ({ ...prev, [key]: { error: true, message } }));
    };

    const isValidSize = (size) => size / 1024 / 1024 <= _MAX_FILE_SIZE;

    const clearError = (key) => {
        if (key in preUploadErrors && preUploadErrors[key].error)
            setPreUploadErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
        else if (key in postUploadErrors && postUploadErrors[key].error)
            setPostUploadErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onUpload = () => {
        if (fileList.length < 1) {
            onExit();
            return;
        }
        if (!isValidSize(totalQueueSize)) {
            configFileError('fileSize', FILE_SIZE_ERROR_MESSAGE);
            return;
        }
        if (!darDocumentId) {
            setPostUploadErrors((prev) => ({
                ...prev,
                darDocumentId: {
                    error: true,
                    message: 'Could not upload documents. DAR document type ID not found'
                }
            }));
            return;
        }
        const payload = new FormData();
        fileList.forEach((el) => {
            payload.append('file', el);
            payload.append('typeId', darDocumentId);
        });
        payload.append('darId', darEntry.id);
        setBackUpFileList([...fileList]);
        setFileList([]);
        setIsPending(true);
        const msg =
            fileList.length === 1
                ? `Document: ${fileList[0].name} has been uploaded`
                : `Documents: ${fileList.map((el) => el.name).join(', ')} have been uploaded`;
        setSuccessMsg(msg);
        dispatch(addParticipantDocuments(darEntry.participantId, payload, msg));
    };

    const onRetryUpload = () => {
        setPostUploadErrors({});
        onUpload();
    };

    const clearUploader = () => {
        setPreUploadErrors({});
        setPostUploadErrors({});
        setPostUploadWarnings([]);
        setWrongFileTypes([]);
        setFileList([]);
        setBackUpFileList([]);
        setTotalQueueSize(0);
        setSuccessMsg('');
        setShowRetryButton(false);
        dispatch(clearDarEntry()); // retry button needs this
        onExit();
    };

    // RENDER
    return (
        <div className={local.uploaderWrapper} data-testid="dar_uploader">
            <label htmlFor="uploader" className={form.formLabel}>
                Attach Documents
            </label>
            <div>
                {isPending && (
                    <div className={uploader.loadingSpinner}>
                        <CircularProgress />
                    </div>
                )}
                <Dragger
                    accept={FILE_TYPES}
                    onDrop={(e) => uploadProps.onDrop(e)}
                    data-testid={`dragger`}
                    {...uploadProps}>
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">Click or drag file(s) to this area to upload</p>
                    <p className="ant-upload-hint">{`Support for fileTypes: ${FILE_TYPES}`}</p>
                </Dragger>
            </div>

            {wrongFileTypes.length > 0 &&
                (wrongFileTypes.length === 1 ? (
                    <div
                        className={uploader.uploaderFileWarning}
                        data-testid="wrong_file_type_warning">
                        <div className={uploader.warningText}>
                            <WarningIcon />
                            This file could not be added as it is the wrong type:{' '}
                            {wrongFileTypes[0].name}
                        </div>
                    </div>
                ) : (
                    <div className={uploader.uploaderFileWarning}>
                        <div className={uploader.warningText}>
                            <WarningIcon />
                            These files could not be added as they are the wrong type:
                        </div>
                        {wrongFileTypes.map((el) => (
                            <div key={el.name} className={uploader.warningRow}>
                                {el.name}
                            </div>
                        ))}
                    </div>
                ))}

            {postUploadWarnings.length > 0 &&
                (postUploadWarnings.length === 1 ? (
                    <div className={uploader.uploaderFileWarning}>
                        <div className={uploader.warningText}>
                            <WarningIcon />
                            This file was not added: {postUploadWarnings[0].filename} - Reason:{' '}
                            {postUploadWarnings[0].errorCode.replaceAll('_', ' ')}
                        </div>
                    </div>
                ) : (
                    <div className={uploader.uploaderFileWarning}>
                        <div className={uploader.warningText}>
                            <WarningIcon />
                            These files were not added:
                        </div>
                        {postUploadWarnings.map((el) => (
                            <div key={el.filename} className={uploader.warningRow}>
                                {el.filename} - Reason: {el.errorCode.replaceAll('_', ' ')}
                            </div>
                        ))}
                    </div>
                ))}

            {Object.values({ ...preUploadErrors, ...postUploadErrors }).some((el) => el.error) && (
                <div>
                    {Object.entries({ ...preUploadErrors, ...postUploadErrors }).map((el) =>
                        el[1].error ? (
                            <div key={el[0]}>
                                <ScreenError
                                    error={el[1]}
                                    name={el[0]}
                                    clearError={clearError}
                                    customClass="relativePos"
                                    allowDismiss={false}
                                />
                            </div>
                        ) : null
                    )}
                </div>
            )}

            {showRetryButton && (
                <div>
                    {Object.keys(postUploadErrors).length > 0 && (
                        <div className={local.retryButtonMessage}>
                            Remove all existing files before adding new files to try again.
                        </div>
                    )}
                    <Button
                        id="retryUpload"
                        type="button"
                        content="Retry Upload"
                        disabled={isRetryDisabled}
                        onClick={onRetryUpload}
                    />
                </div>
            )}
        </div>
    );
};

DarDocumentUploader.propTypes = {
    onUploadErrors: PropTypes.func,
    onPreUploadErrors: PropTypes.func,
    isOpenUploader: PropTypes.bool,
    onExit: PropTypes.func
};

export default DarDocumentUploader;
